import RevisableLanguagePack from "../../../../../lib/html5-software-library/src/ranj/language/RevisableLanguagePack";
import LayoutManager from "../../../../../lib/html5-software-library/src/ranj/layout/LayoutManager";
import Config from "../../../core/config/Config";
import AbstractMediator from "../../../core/ui/AbstractMediator";
import ClickEaterFactory from "../../../core/ui/ClickEaterFactory";
import ScormController from "../../../scorm/controller/ScormController";
import CharacterLibrary from "../../characters/model/CharacterLibrary";
import PlayerModel from "../../player/model/PlayerModel";
import ProgressModel from "../../progress/model/ProgressModel";
import MovieClipUtil from "../../util/MovieClipUtil";
import PromiseUtil from "../../util/PromiseUtil";
import TrackerScore from "../../score/model/TrackerScore";
import SoundManager from "../../../../../lib/html5-software-library/src/ranj/audio/SoundManager";
import SoundType from "../../../core/sound/util/SoundType";

export default class EndOfGameScreenScoreMediator extends AbstractMediator {
    /**
     * @constructor
     * @param {createjs.MovieClip} target 
     * @param {soma.EventDispatcher} dispatcher 
     * @param {LayoutManager} layoutManager 
     * @param {ClickEaterFactory} clickEaterFactory 
     * @param {PlayerModel} playerModel
     * @param {RevisableLanguagePack} languagePack 
     * @param {Config} config 
     * @param {ScormController} scormController 
     * @param {ProgressModel} progressModel 
     * @param {CharacterLibrary} characterLibrary 
     * @param {SoundManager} soundManager 
     */
    constructor(target, dispatcher, layoutManager, clickEaterFactory, languagePack, soundManager) {
        super(target, dispatcher, layoutManager, clickEaterFactory);

        this._languagePack = languagePack;
        this._soundManager = soundManager;

        /** @type {TrackerScore[]} */
        this._trackerScores = [];
        /** @type {TrackerScore} */
        this._primaryTrackerScore = null;
    }

    initialize() {
        super.initialize();

        this._target.groupSpiritTextField.lineWidth = null;
        this._target.trustTextField.lineWidth = null;

        this._setLanguagePackTexts();

        this.preventClickThrough();
    }

    destroy() {
        super.destroy();
    }

    /**
     * @param {object} params
     * @param {TrackerScore[]} params.trackerScores
     * @param {TrackerScore[]} params.primaryTrackerScore
     */
    populate({ trackerScores, primaryTrackerScore }) {
        this._trackerScores = trackerScores;
        this._primaryTrackerScore = primaryTrackerScore

        const groupSpiritScore = this._trackerScores.find(trackerScore => trackerScore.id === `groupSpirit`);
        this._target.groupSpiritTextField.text = `${groupSpiritScore.getRoundedPercentage()}% ` + this._languagePack.getText(`endOfGame.scorePanel.groupSpirit`);

        const trustScore = this._trackerScores.find(trackerScore => trackerScore.id === `trust`);
        this._target.trustTextField.text = `${trustScore.getRoundedPercentage()}% ` + this._languagePack.getText(`endOfGame.scorePanel.trust`);
    }

    hideInstant() {
        this._target.alpha = 0;
        this._target.titleTextField.alpha = 0;
        this._target.scoreMeter.alpha = 0;
        this._target.scoreMeter.scale = .8;

        this._target.groupSpiritTextField.alpha = 0;
        this._target.trustTextField.alpha = 0;

        this._target.scoreMeter.gotoAndStop(0);
    }

    async show() {
        // Animates background.
        await createjs.Tween.get(this._target)
            .to({ alpha: 1, }, 650, createjs.Ease.sineInOut)
            .toPromise();

        // Animates first batch of text and meters.
        await createjs.Tween.get(this._target.titleTextField)
            .to({ alpha: 1, }, 650, createjs.Ease.sineInOut)
            .toPromise();

        this._soundManager.playSound(SoundType.END_OF_GAME_METER_SHOW);
        await createjs.Tween.get(this._target.scoreMeter)
            .wait(250)
            .to({ alpha: 1, scale: 1, }, 800, createjs.Ease.backOut)
            .toPromise();
        await PromiseUtil.delay(550);

        await this._animateScoreMeter();

        // Animates second batch of (secondary) texts.
        createjs.Tween.get(this._target.groupSpiritTextField)
            .to({ alpha: 1, }, 650, createjs.Ease.sineInOut)
            .toPromise();
        await createjs.Tween.get(this._target.trustTextField)
            .to({ alpha: 1, }, 650, createjs.Ease.sineInOut)
            .toPromise();
    }

    async transitionPartiallyOut() {
        createjs.Tween.get(this._target.titleTextField)
            .to({ alpha: 1, }, 650, createjs.Ease.sineInOut)
            .toPromise();

        this._soundManager.playSound(SoundType.END_OF_GAME_METER_HIDE);
        createjs.Tween.get(this._target.scoreMeter)
            .to({ alpha: 1, }, 650, createjs.Ease.sineInOut)
            .toPromise();
    }

    _setLanguagePackTexts() {
        this._target.titleTextField.text = this._languagePack.getText(`endOfGame.scorePanel.title`);
        this._target.scoreMeter.teamMeterText.text = this._languagePack.getText(`endOfGame.generic.teamScoreMeter`);
    }

    async _animateScoreMeter() {
        const targetFrame = this._primaryTrackerScore.getRoundedPercentage();
        const duration = targetFrame * 25;

        console.log(`Animating score meter for ${duration}ms to frame ${targetFrame}`);
        const meterFillingSoundId = this._soundManager.playSound(SoundType.END_OF_GAME_METER_FILLING);
        // Stop meter filling sound 150ms before the animation ends.
        createjs.Tween.get({})
            .to({}, Math.max(duration - 150, 0), createjs.Ease.linear)
            .call(() => {
                console.log('Stopping meter filling sound');
                this._soundManager.stopSound(meterFillingSoundId, 100);
            });

        const tween = MovieClipUtil.animateToFrame(this._target.scoreMeter, targetFrame, duration, createjs.Ease.sineInOut, ({ currentFrame }) => {
            const roundedCurrentFrame = Math.round(currentFrame);
            this._target.scoreMeter.glow.gotoAndStop(roundedCurrentFrame);
        });

        await tween.toPromise();
    }
}